import { useCallback, useEffect, useRef, useState } from "react";
import {
  cloudWechatCreateSession,
  cloudWechatLoginStatus,
  cloudWechatPollSession,
} from "../api/client";

export type WechatAuthUser = {
  email: string;
  name?: string;
  role: string;
  tenantId: string;
  tenantName?: string;
  phoneBound?: boolean;
  wxBound?: boolean;
};

type WxLoginOptions = {
  self_redirect?: boolean | string;
  id: string;
  appid: string;
  scope: string;
  redirect_uri: string;
  state: string;
  style?: string;
  href?: string;
  /** 1 = 新版 UI（此时 href 自定义样式失效） */
  stylelite?: number;
  /** 0 = 禁用桌面端微信快捷登录，仅显示二维码 */
  fast_login?: number;
  onReady?: (ready: boolean) => void;
};

declare global {
  interface Window {
    WxLogin?: new (opts: WxLoginOptions) => void;
  }
}

function loadWxLoginScript(): Promise<void> {
  if (window.WxLogin) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-wx-login="1"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("wx_script_failed")),
      );
      return;
    }
    const s = document.createElement("script");
    s.src = "https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js";
    s.async = true;
    s.dataset.wxLogin = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("wx_script_failed"));
    document.head.appendChild(s);
  });
}

/** 仅缩小二维码；不整体 scale iframe，快捷登录保持原生大小 */
function wxLoginHrefCssUrl(): string {
  if (typeof window === "undefined") return "/wx-login.css";
  return `${window.location.origin}/wx-login.css`;
}

type Props = {
  qrBoxId: string;
  lead?: string;
  autoStart?: boolean;
  /** login = 登录页；bind = 个人中心绑定到当前账号 */
  mode?: "login" | "bind";
  /**
   * false = 关闭桌面快捷登录，只出二维码。
   * 默认 true：有桌面微信时显示快捷登录（与二维码分开展示，互不 scale）。
   */
  enableFastLogin?: boolean;
  onConfirmed: (payload: {
    accessToken: string;
    user: WechatAuthUser | null;
    tenant: { id: string; name: string } | null;
  }) => void | Promise<void>;
  onNeedBind?: (info: {
    sessionId: string;
    bindToken: string;
    nickname: string | null;
  }) => void;
};

export function WechatQrAuth({
  qrBoxId,
  lead = "扫码登录；未绑定微信时请用已有账号完成绑定",
  autoStart = true,
  mode = "login",
  enableFastLogin = true,
  onConfirmed,
  onNeedBind,
}: Props) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const pollRef = useRef<number | null>(null);
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;
  const onNeedBindRef = useRef(onNeedBind);
  onNeedBindRef.current = onNeedBind;

  const stopPoll = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPoll = useCallback(
    (id: string) => {
      stopPoll();
      pollRef.current = window.setInterval(() => {
        void (async () => {
          try {
            const s = await cloudWechatPollSession(id);
            if (s.status === "confirmed" && s.accessToken) {
              stopPoll();
              await onConfirmedRef.current({
                accessToken: s.accessToken,
                user: s.user
                  ? {
                      email: s.user.email,
                      name: s.user.name,
                      role: s.user.role,
                      tenantId: s.user.tenantId,
                      tenantName: s.user.tenantName,
                      phoneBound: Boolean(s.user.phoneBound),
                      wxBound: s.user.wxBound !== false,
                    }
                  : null,
                tenant: s.tenant || s.user?.tenant || null,
              });
              return;
            }
            if (s.status === "expired" || s.status === "error") {
              stopPoll();
              setHint(s.message || "扫码失败，请刷新二维码");
            }
            if (s.status === "need_bind") {
              stopPoll();
              if (s.bindToken && onNeedBindRef.current) {
                onNeedBindRef.current({
                  sessionId: id,
                  bindToken: s.bindToken,
                  nickname: s.nickname || null,
                });
                return;
              }
              setHint("该微信尚未绑定账号，请用已有邮箱或手机号绑定");
            }
          } catch {
            /* keep polling */
          }
        })();
      }, 2500);
    },
    [stopPoll],
  );

  const startQr = useCallback(async () => {
    setHint("");
    setBusy(true);
    stopPoll();
    try {
      const session = await cloudWechatCreateSession(
        mode === "bind" ? "bind" : undefined,
      );
      setSessionId(session.sessionId);
      await loadWxLoginScript();
      const box = document.getElementById(qrBoxId);
      if (box) box.innerHTML = "";
      if (!window.WxLogin) {
        setHint("请在新窗口完成微信授权");
        window.open(
          session.authorizeUrl,
          "_blank",
          "noopener,width=480,height=640",
        );
      } else {
        // stylelite 默认旧 UI，href 才能单独缩二维码；快捷登录走 fast_login，不被外层 scale
        new window.WxLogin({
          self_redirect: false,
          id: qrBoxId,
          appid: session.appId,
          scope: "snsapi_login",
          redirect_uri: encodeURIComponent(session.redirectUri),
          state: session.state,
          style: "black",
          href: wxLoginHrefCssUrl(),
          fast_login: enableFastLogin ? 1 : 0,
          onReady: () => {
            const iframe = document
              .getElementById(qrBoxId)
              ?.querySelector("iframe");
            if (iframe) {
              iframe.setAttribute("allow", "local-network-access");
              iframe.style.border = "0";
              iframe.style.display = "block";
              iframe.style.margin = "0 auto";
            }
          },
        });
      }
      startPoll(session.sessionId);
    } catch (err) {
      setHint(err instanceof Error ? err.message : "无法发起微信扫码");
    } finally {
      setBusy(false);
    }
  }, [enableFastLogin, mode, qrBoxId, startPoll, stopPoll]);

  useEffect(() => {
    let cancelled = false;
    void cloudWechatLoginStatus()
      .then((s) => {
        if (!cancelled) setConfigured(Boolean(s.configured));
      })
      .catch(() => {
        if (!cancelled) setConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!autoStart || !configured || sessionId) {
      return () => stopPoll();
    }
    void startQr();
    return () => stopPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, configured]);

  useEffect(() => () => stopPoll(), [stopPoll]);

  if (configured === false) {
    return (
      <p className="login-hint">
        平台尚未配置微信开放平台网站应用。请联系运营在「API 配置 → 微信」填写
        AppID / AppSecret。
      </p>
    );
  }

  return (
    <div className="login-wechat">
      {lead ? <p className="login-wechat-lead">{lead}</p> : null}
      <div id={qrBoxId} className="login-wx-qr" />
      {busy ? (
        <p
          className="muted"
          style={{ textAlign: "center", margin: "0 0 4px", fontSize: 12 }}
        >
          正在加载二维码…
        </p>
      ) : null}
      {hint ? (
        <p className="login-hint" style={{ marginTop: 0, marginBottom: 8 }}>
          {hint}
        </p>
      ) : null}
      <button
        type="button"
        className="btn btn-ghost btn-sm login-wx-refresh"
        disabled={busy || configured === null}
        onClick={() => {
          setSessionId(null);
          void startQr();
        }}
      >
        刷新二维码
      </button>
    </div>
  );
}
